import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IXrayAnalysis } from 'src/ts/interfaces/xray';
import { XrayAnalysisState, XrayStatus } from './useXrayAnalysis';

export type CandidateXrayResult = Omit<XrayAnalysisState, 'status' | 'retry'> & {
  status: XrayStatus | 'unavailable';
};

export interface CandidateXrayState {
  resultFor: (checkKey: string, rowId: string) => CandidateXrayResult;
  retry: (checkKey: string, rowId: string) => void;
  preload: (checkKey: string, rowIds: string[], shownRowId: string | null) => void;
}

interface QueuedCandidate {
  proofId: string;
  checkKey: string;
  rowId: string;
}

const IDLE: CandidateXrayResult = { status: 'idle', data: null, error: null };
const LOADING: CandidateXrayResult = { status: 'loading', data: null, error: null };
const FAILED: CandidateXrayResult = {
  status: 'error',
  data: null,
  error: 'X-Ray analysis failed'
};
const UNAVAILABLE: CandidateXrayResult = { status: 'unavailable', data: null, error: null };

const keyOf = ({ proofId, checkKey, rowId }: QueuedCandidate) => `${proofId}|${checkKey}|${rowId}`;

/**
 * X-Ray analysis of list check candidates' example PDFs for the proof on
 * screen. `preload` queues a check's candidates. The candidate on screen starts
 * right away, whatever else is loading, so stepping never waits behind another
 * candidate; the rest load one at a time in the background. Switching proofs
 * drops the old proof's queue and its requests stop counting against the
 * background slot. Each candidate is requested once per review unless retried,
 * and unmounting aborts whatever is still in flight.
 */
export const useCandidateXray = (
  proofId: string | undefined,
  enabled: boolean
): CandidateXrayState => {
  const requestedRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<Map<string, boolean>>(new Map());
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const [queue, setQueue] = useState<QueuedCandidate[]>([]);
  const [shown, setShown] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, CandidateXrayResult>>({});
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    const cancelSource = cancelSourceRef.current;
    return () => {
      cancelSource.cancel();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !proofId) return;
    const requested = requestedRef.current;
    const inFlight = inFlightRef.current;
    const waiting = (candidate: QueuedCandidate) =>
      candidate.proofId === proofId && !requested.has(keyOf(candidate));

    const start = (next: QueuedCandidate, background: boolean) => {
      const key = keyOf(next);
      requested.add(key);
      inFlight.set(key, background);
      setResults((prev) => ({ ...prev, [key]: LOADING }));

      const fetchCandidate = async () => {
        let outcome: CandidateXrayResult;
        try {
          const body: { data: IXrayAnalysis } = await defaultInstance.get(
            `${configVars.server_url}/v2/proofs/${
              next.proofId
            }/list_check_rulings/${encodeURIComponent(
              next.checkKey
            )}/candidates/${encodeURIComponent(next.rowId)}/xray`,
            { cancelToken: cancelSourceRef.current.token }
          );
          outcome = { status: 'success', data: body.data, error: null };
        } catch (error) {
          if (axios.isCancel(error)) {
            inFlight.delete(key);
            return;
          }
          const noExample = axios.isAxiosError(error) && error.response?.status === 422;
          outcome = noExample ? UNAVAILABLE : FAILED;
        }
        inFlight.delete(key);
        setResults((prev) => ({ ...prev, [key]: outcome }));
        setCompleted((previous) => previous + 1);
      };

      fetchCandidate();
    };

    const shownCandidate = queue.find((candidate) => keyOf(candidate) === shown);
    if (shownCandidate && waiting(shownCandidate)) start(shownCandidate, false);

    const backgroundBusy = Array.from(inFlight).some(
      ([key, background]) => background && key.startsWith(`${proofId}|`)
    );
    const next = queue.find(waiting);
    if (next && !backgroundBusy) start(next, true);
  }, [queue, shown, proofId, enabled, completed]);

  const preload = useCallback(
    (checkKey: string, rowIds: string[], shownRowId: string | null) => {
      if (!proofId) return;
      const added = rowIds.map((rowId) => ({ proofId, checkKey, rowId }));
      const addedKeys = new Set(added.map(keyOf));
      setQueue((prev) => [
        ...added,
        ...prev.filter((queued) => queued.proofId === proofId && !addedKeys.has(keyOf(queued)))
      ]);
      setShown(shownRowId ? keyOf({ proofId, checkKey, rowId: shownRowId }) : null);
    },
    [proofId]
  );

  const retry = useCallback(
    (checkKey: string, rowId: string) => {
      if (!proofId) return;
      const candidate = { proofId, checkKey, rowId };
      const key = keyOf(candidate);
      if (inFlightRef.current.has(key)) return;
      requestedRef.current.delete(key);
      setResults((prev) => ({ ...prev, [key]: IDLE }));
      setQueue((prev) => [candidate, ...prev.filter((queued) => keyOf(queued) !== key)]);
      setShown(key);
    },
    [proofId]
  );

  const resultFor = useCallback(
    (checkKey: string, rowId: string) =>
      (proofId && results[keyOf({ proofId, checkKey, rowId })]) || IDLE,
    [proofId, results]
  );

  return { resultFor, retry, preload };
};
